import { useEffect, useState } from 'react';

export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * Centre estimé pour initialiser la carte (ADR-0040, C6) : **position du navigateur** d'abord
 * (géolocalisation approximative, côté client — aucun service tiers, souverain), puis **repli** sur la
 * localisation approximative du profil. `null` tant qu'aucune des deux n'est disponible (ni refusée).
 *
 * La géolocalisation demande le consentement du navigateur ; en cas de refus/indisponibilité on bascule
 * sur le repli sans bruit. Volontairement grossière (`enableHighAccuracy: false`, cache accepté).
 */
export function useEstimatedCenter(fallback: LatLon | null): LatLon | null {
  const [center, setCenter] = useState<LatLon | null>(null);
  const fLat = fallback?.lat ?? null;
  const fLon = fallback?.lon ?? null;

  useEffect(() => {
    let active = true;
    const applyFallback = () => {
      if (active && fLat !== null && fLon !== null) setCenter({ lat: fLat, lon: fLon });
    };
    if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (active) setCenter({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        },
        () => applyFallback(),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 },
      );
    } else {
      applyFallback();
    }
    return () => {
      active = false;
    };
  }, [fLat, fLon]);

  return center;
}
